import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabase from '../../supabase/supabase';
import { emailService } from '../../services/emailService';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { cn } from '../../components/ui/Button';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

const libraries = ['places'];
const LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780693784/BOTG_Logo_o9fcqe.png";

export default function ManageBooking({ type = 'customer' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [formData, setFormData] = useState({ address: '', booking_date: '', booking_time: '' });
  const [mapCenter, setMapCenter] = useState({ lat: 6.9271, lng: 79.8612 });

  // Custom cancel modal state
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const { toast, showToast, hideToast } = useToast();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries
  });

  useEffect(() => {
    const fetchBooking = async () => {
      const uuidMatch = id?.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
      const cleanId = uuidMatch ? uuidMatch[0] : null;

      if (!cleanId) {
        setFetchError("Invalid booking link format.");
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('bookings_1740423033529')
          .select(`
            *,
            customer:customer_id(full_name, email, phone),
            barber:barber_id(full_name, email, phone),
            service:service_id(name)
          `)
          .eq('id', cleanId)
          .single();

        if (error) throw error;
        if (!data) throw new Error("Booking not found");

        setBooking(data);
        setFormData({
          address: data.address || '',
          booking_date: data.booking_date || '',
          booking_time: data.booking_time || ''
        });
      } catch (err) {
        setFetchError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id]);

  useEffect(() => {
    if (isLoaded && booking?.address && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: booking.address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setMapCenter({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
        }
      });
    }
  }, [isLoaded, booking?.address]);

  const handleOpenMaps = () => {
    if (booking?.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(booking.address)}`, '_blank');
    }
  };

  const handleCall = () => {
    const phone = type === 'customer' ? booking.barber?.phone : booking.customer?.phone;
    if (phone) window.location.href = `tel:${phone}`;
    else showToast("Phone number not available.", "error");
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const needsReapproval = formData.booking_date !== booking.booking_date || formData.booking_time !== booking.booking_time;
      const newStatus = needsReapproval ? 'pending' : booking.status;

      const { error } = await supabase
        .from('bookings_1740423033529')
        .update({
          address: formData.address,
          booking_date: formData.booking_date,
          booking_time: formData.booking_time,
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;
      showToast(needsReapproval ? "Booking rescheduled. Marked as pending for barber approval." : "Booking updated successfully.", "success");
      setBooking(prev => ({ ...prev, ...formData, status: newStatus }));
    } catch (err) {
      showToast("Failed to update: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === 'cancelled') {
      setCancelModal(true);
      return;
    }

    setSaving(true);
    try {
      // When completing, auto-set payment_status to 'paid' (cash collected on service)
      const extraUpdates = {};
      if (newStatus === 'completed') {
        extraUpdates.payment_status = 'paid';
      }

      const { error } = await supabase
        .from('bookings_1740423033529')
        .update({ status: newStatus, ...extraUpdates, updated_at: new Date().toISOString() })
        .eq('id', booking.id);

      if (error) throw error;
      setBooking(prev => ({ ...prev, status: newStatus, ...extraUpdates }));

      const details = {
        customerEmail: booking.customer?.email,
        customerName: booking.customer?.full_name,
        barberName: booking.barber?.full_name,
        serviceName: booking.service?.name,
        date: booking.booking_date,
        time: booking.booking_time,
        address: booking.address
      };

      if (newStatus === 'confirmed' && booking.customer?.email) {
        await emailService.sendBookingConfirmedToCustomer({
          ...details,
          manageLink: `${window.location.origin}/booking/c/${booking.id}`
        });
      } else if (newStatus === 'completed' && booking.customer?.email) {
        await emailService.sendBookingCompleted({
          ...details,
          manageLink: `${window.location.origin}/booking/c/${booking.id}`
        });
      }

      showToast(`Booking marked as ${newStatus}.`, "success");
    } catch (err) {
      showToast("Failed to update status: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const executeCancel = async () => {
    if (!cancelReason.trim()) {
      showToast("Please provide a cancellation reason.", "error");
      return;
    }

    setCancelling(true);
    try {
      const { error } = await supabase
        .from('bookings_1740423033529')
        .update({
          status: 'cancelled',
          cancellation_reason: cancelReason.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;
      setBooking(prev => ({ ...prev, status: 'cancelled', cancellation_reason: cancelReason.trim() }));

      const details = {
        customerEmail: booking.customer?.email,
        customerName: booking.customer?.full_name,
        barberName: booking.barber?.full_name,
        serviceName: booking.service?.name,
        date: booking.booking_date,
        time: booking.booking_time,
        address: booking.address,
        reason: cancelReason.trim()
      };

      const targetEmail = type === 'barber' ? booking.customer?.email : booking.barber?.email;
      if (targetEmail) {
        await emailService.sendBookingCancelled({
          ...details,
          manageLink: `${window.location.origin}/booking/${type === 'barber' ? 'c' : 'b'}/${booking.id}`
        }, targetEmail);
      }

      setCancelModal(false);
      setCancelReason('');
      showToast('Booking cancelled successfully.', 'success');
    } catch (err) {
      showToast("Failed to cancel: " + err.message, "error");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="p-20 text-center">Syncing with BookOTG Network...</div>;

  if (fetchError) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-3xl text-center max-w-md shadow-sm border border-gray-100">
        <SafeIcon name="AlertCircle" className="text-4xl text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Error Loading Booking</h2>
        <p className="text-gray-500 mb-6">{fetchError}</p>
        <Button onClick={() => navigate('/')}>Return Home</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-10 cursor-pointer" onClick={() => navigate('/')}>
          <img src={LOGO_URL} alt="BookOTG" className="h-14 object-contain" />
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="p-8 border-b border-gray-50 bg-gray-50/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-black text-brand-dark tracking-tight">Booking Overview</h1>
              <p className="text-xs text-gray-500 font-mono">ID: {booking.id.split('-')[0]}</p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={handleCall}
                className="flex-1 sm:flex-none rounded-full px-6 py-2.5 h-auto text-[10px] bg-green-600 hover:bg-green-700 text-white font-black uppercase tracking-widest"
              >
                <SafeIcon name="Phone" className="mr-2" />
                Call {type === 'customer' ? 'Barber' : 'Client'}
              </Button>
              {type === 'barber' && (
                <Button
                  onClick={handleOpenMaps}
                  className="flex-1 sm:flex-none rounded-full px-6 py-2.5 h-auto text-[10px] bg-brand-primary text-white font-black uppercase tracking-widest"
                >
                  <SafeIcon name="Map" className="mr-2" />
                  Navigate
                </Button>
              )}
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-3xl border border-gray-100">
              <div>
                <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest mb-1">Service</p>
                <p className="font-bold text-brand-dark text-lg">{booking.service?.name}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest mb-1">Total Due</p>
                <p className="font-black text-brand-dark text-lg">Rs. {Number(booking.total_price || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest mb-1">Schedule</p>
                <p className="font-bold text-brand-dark">{booking.booking_date} at {booking.booking_time}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest mb-1">{type === 'customer' ? 'Barber' : 'Customer'}</p>
                <p className="font-bold text-brand-dark">{type === 'customer' ? booking.barber?.full_name : booking.customer?.full_name}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest mb-1">Service Location</p>
                <p className="font-bold text-gray-600">{booking.address}</p>
              </div>
            </div>

            {/* Map */}
            {isLoaded && mapCenter && (
              <div className="rounded-3xl overflow-hidden h-[300px] border border-gray-200 shadow-inner">
                <GoogleMap
                  zoom={15}
                  center={mapCenter}
                  mapContainerClassName="w-full h-full"
                  options={{ disableDefaultUI: true, zoomControl: true }}
                >
                  <Marker position={mapCenter} />
                </GoogleMap>
              </div>
            )}

            {booking.status === 'pending' || booking.status === 'confirmed' ? (
              <div className="space-y-6">
                {/* Status Control */}
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                  <h3 className="font-bold text-brand-dark mb-4 text-center">Status Control</h3>
                  <div className="flex flex-wrap gap-3">
                    {type === 'barber' && booking.status === 'pending' && (
                      <Button
                        onClick={() => handleStatusChange('confirmed')}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white border-none py-4 font-black"
                      >
                        <SafeIcon name="Check" className="mr-2" />
                        Approve Request
                      </Button>
                    )}
                    {type === 'barber' && booking.status === 'confirmed' && (
                      <Button
                        onClick={() => handleStatusChange('completed')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white border-none py-4 font-black"
                      >
                        <SafeIcon name="CheckCircle" className="mr-2" />
                        Mark Completed
                      </Button>
                    )}
                    <Button
                      onClick={() => handleStatusChange('cancelled')}
                      variant="danger"
                      className="flex-1 bg-red-50 text-red-600 hover:bg-red-100 border-none py-4 font-black"
                    >
                      <SafeIcon name="XCircle" className="mr-2" />
                      Cancel Booking
                    </Button>
                  </div>
                </div>

                {/* Update Form */}
                <form onSubmit={handleUpdate} className="space-y-6 bg-white p-6 rounded-3xl border border-gray-100">
                  <h3 className="font-bold text-brand-dark mb-4">Update Details</h3>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Service Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={e => setFormData({ ...formData, address: e.target.value })}
                      className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-primary font-medium"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Date</label>
                      <input
                        type="date"
                        value={formData.booking_date}
                        onChange={e => setFormData({ ...formData, booking_date: e.target.value })}
                        className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-primary font-medium"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Time</label>
                      <input
                        type="time"
                        value={formData.booking_time}
                        onChange={e => setFormData({ ...formData, booking_time: e.target.value })}
                        className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-primary font-medium"
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" variant="secondary" className="w-full py-4 rounded-2xl font-black" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Updates'}
                  </Button>
                </form>
              </div>
            ) : (
              <div className="p-10 bg-gray-50 rounded-3xl border border-gray-200 text-center">
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4",
                  booking.status === 'completed' ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                )}>
                  <SafeIcon name={booking.status === 'completed' ? "Check" : "X"} className="text-xl" />
                </div>
                <p className="text-sm font-black text-gray-500 uppercase tracking-widest">This booking is {booking.status}</p>
                {booking.cancellation_reason && (
                  <p className="mt-2 text-xs text-red-400 italic">Reason: {booking.cancellation_reason}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Cancel Modal */}
      {cancelModal && (
        <div className="fixed inset-0 bg-brand-dark/60 z-50 flex items-center justify-center p-6 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="XCircle" className="text-3xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark mb-2 text-center">Cancel Booking</h2>
            <p className="text-gray-500 font-medium text-sm mb-6 text-center leading-relaxed">
              Please provide a reason for cancellation. This will be shared with the {type === 'barber' ? 'customer' : 'barber'}.
            </p>
            <textarea
              placeholder="Reason for cancellation (required)..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl mb-6 text-sm font-medium outline-none focus:border-red-500 min-h-[100px]"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setCancelModal(false); setCancelReason(''); }}
              >
                Go Back
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                disabled={!cancelReason.trim() || cancelling}
                onClick={executeCancel}
              >
                {cancelling ? 'Cancelling...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}